import {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
} from "react";
import { useLocalStorage } from "./hooks/useLocalStorage";

// Define available themes
export const THEMES = {
	LIGHT: "light",
	DARK: "dark",
	MONOKAI: "monokai",
	DRACULA: "dracula",
	NORD: "nord",
	SOLARIZED: "solarized",
	GITHUB: "github",
	NIGHTOWL: "nightowl",
	SYNTHWAVE: "synthwave",
	AURA: "aura",
};

export const THEME_MODES = {
	LIGHT: "light",
	DARK: "dark",
	SYSTEM: "system",
};

export const LIGHT_THEMES = [THEMES.LIGHT, THEMES.GITHUB, THEMES.SOLARIZED];

export const DARK_THEMES = [
	THEMES.DARK,
	THEMES.MONOKAI,
	THEMES.DRACULA,
	THEMES.NORD,
	THEMES.NIGHTOWL,
	THEMES.SYNTHWAVE,
	THEMES.AURA,
];

export const THEME_ORDER = [...LIGHT_THEMES, ...DARK_THEMES];

const ThemeContext = createContext(null);

const getSystemThemeMode = () => {
	if (typeof window !== "undefined" && window.matchMedia) {
		return window.matchMedia("(prefers-color-scheme: dark)").matches
			? THEME_MODES.DARK
			: THEME_MODES.LIGHT;
	}
	return THEME_MODES.LIGHT;
};

const getStoredTheme = () => {
	if (typeof localStorage === "undefined") {
		return null;
	}

	try {
		const storedTheme = localStorage.getItem("theme");
		return storedTheme !== null ? JSON.parse(storedTheme) : null;
	} catch (error) {
		console.error('Error reading localStorage key "theme":', error);
		return null;
	}
};

const getThemeMode = (theme) =>
	LIGHT_THEMES.includes(theme) ? THEME_MODES.LIGHT : THEME_MODES.DARK;

const isTheme = (theme) => THEME_ORDER.includes(theme);

export const ThemeProvider = ({ children }) => {
	const [storedTheme] = useState(getStoredTheme);
	const storedThemeMode = isTheme(storedTheme)
		? getThemeMode(storedTheme)
		: null;
	const [systemThemeMode, setSystemThemeMode] = useState(getSystemThemeMode);
	const [themeMode, setThemeMode] = useLocalStorage(
		"themeMode",
		storedThemeMode ?? THEME_MODES.SYSTEM,
	);
	const [lightTheme, setLightTheme] = useLocalStorage(
		"lightTheme",
		LIGHT_THEMES.includes(storedTheme) ? storedTheme : THEMES.LIGHT,
	);
	const [darkTheme, setDarkTheme] = useLocalStorage(
		"darkTheme",
		DARK_THEMES.includes(storedTheme) ? storedTheme : THEMES.DARK,
	);

	const resolvedThemeMode =
		themeMode === THEME_MODES.SYSTEM ? systemThemeMode : themeMode;
	const theme =
		resolvedThemeMode === THEME_MODES.LIGHT ? lightTheme : darkTheme;

	const setTheme = useCallback(
		(nextTheme) => {
			if (!isTheme(nextTheme)) {
				return;
			}

			const nextThemeMode = getThemeMode(nextTheme);
			setThemeMode(nextThemeMode);
			if (nextThemeMode === THEME_MODES.LIGHT) {
				setLightTheme(nextTheme);
			} else {
				setDarkTheme(nextTheme);
			}
		},
		[setDarkTheme, setLightTheme, setThemeMode],
	);

	const contextValue = useMemo(
		() => ({
			theme,
			setTheme,
			themeMode,
			setThemeMode,
			resolvedThemeMode,
			systemThemeMode,
			lightTheme,
			setLightTheme,
			darkTheme,
			setDarkTheme,
		}),
		[
			darkTheme,
			lightTheme,
			resolvedThemeMode,
			setDarkTheme,
			setLightTheme,
			setTheme,
			setThemeMode,
			systemThemeMode,
			theme,
			themeMode,
		],
	);

	useEffect(() => {
		if (isTheme(storedTheme)) {
			localStorage.removeItem("theme");
		}
	}, [storedTheme]);

	useEffect(() => {
		if (typeof window === "undefined" || !window.matchMedia) {
			return undefined;
		}

		const media = window.matchMedia("(prefers-color-scheme: dark)");
		const handleChange = (event) => {
			setSystemThemeMode(event.matches ? THEME_MODES.DARK : THEME_MODES.LIGHT);
		};

		media.addEventListener("change", handleChange);
		return () => media.removeEventListener("change", handleChange);
	}, []);

	useEffect(() => {
		// Remove all theme classes first
		document.documentElement.classList.remove(...THEME_ORDER);

		// Add the current theme class
		if (theme !== THEMES.LIGHT) {
			document.documentElement.classList.add(theme);
		}

		document.documentElement.style.colorScheme = LIGHT_THEMES.includes(theme)
			? "light"
			: "dark";
	}, [theme]);

	return (
		<ThemeContext.Provider value={contextValue}>
			{children}
		</ThemeContext.Provider>
	);
};

export const useTheme = () => {
	const context = useContext(ThemeContext);
	if (context === null) {
		throw new Error("useTheme must be used within a ThemeProvider");
	}
	return context;
};
